import { Icon } from '@iconify/react';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import {
  CollapsibleCard,
  ConfirmationModal,
  StyledGrid,
  StyledGridItem,
} from 'modules/shared/components';
import { PRIORITY_COLORS, PRIORITY_LABELS } from 'modules/shared/constants';
import { TaskName } from 'modules/work-implementation/project-view/shared/components';
import { useCallback, useState } from 'react';

/**
 * Format time string to hours:minutes
 * @param {string} timeString
 * @returns {string}
 */
const formatTimeString = (timeString = '00:00:00') => {
  const [h = '00', m = '00'] = timeString.split(':');
  return `${h}hrs:${m}mins`;
};

/**
 * AssignedTaskView component
 * @param {Object} props
 * @param {Object} props.taskMetadata
 * @param {Object} props.task
 * @param {Object} props.projectData
 * @returns {JSX.Element}
 */
export default function AssignedTaskView({
  taskMetadata,
  task,
  projectData,
  onEdit,
  onDelete,
  isLocked,
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const employeeName = task?.employee?.name;
  const taskKey = taskMetadata.taskKey;

  const handleEdit = useCallback(
    (e) => {
      e.stopPropagation();
      onEdit(taskKey);
    },
    [onEdit, taskKey],
  );

  const handleDeleteConfirm = useCallback(
    (e) => {
      e.stopPropagation();
      setConfirmDelete(false);
      onDelete(task.id);
    },
    [onDelete, task.id],
  );

  if (!task) return null;

  return (
    <>
      <CollapsibleCard
        title={<TaskName taskName={taskMetadata.name} />}
        meta={
          <div className='row gap-3'>
            <button
              type='button'
              className='btn text-btn text-btn--medium'
              onClick={handleEdit}
              disabled={isLocked}
            >
              <Icon icon='carbon:edit' />
              Edit
            </button>
            <button
              type='button'
              className='btn text-btn text-btn--medium'
              onClick={() => setConfirmDelete(true)}
              disabled={isLocked}
            >
              <Icon icon='ic:outline-delete' />
              Delete
            </button>
          </div>
        }
        borderVariant='outlined'
        colorVariant='default'
      >
        <div className='column'>
          <StyledGrid columns={3}>
            <StyledGridItem columnIndex={0}>
              <span>Job code</span>
              <span>{projectData.code}</span>
            </StyledGridItem>
            <StyledGridItem columnIndex={1}>
              <span>Project name</span>
              <span>{projectData.name}</span>
            </StyledGridItem>
            <StyledGridItem columnIndex={2}>
              <span>Employee</span>
              {employeeName ? (
                <span>{employeeName}</span>
              ) : (
                <span className='unavailable-message'>Not Available</span>
              )}
            </StyledGridItem>
          </StyledGrid>

          <StyledGrid columns={3}>
            <StyledGridItem columnIndex={0}>
              <span>Work timeline</span>
              <span>{formatTimeString(task.allottedTime)}</span>
            </StyledGridItem>
            <StyledGridItem columnIndex={1}>
              <span>Priority</span>
              <Typography
                component='span'
                sx={{ color: PRIORITY_COLORS[task.priority], fontWeight: 600 }}
              >
                {PRIORITY_LABELS[task.priority]}
              </Typography>
            </StyledGridItem>
            <StyledGridItem columnIndex={2}>
              <span className='self-start'>Inward documents</span>
              <div className='column gap-1'>
                {task.documentsShared?.length === 0 && (
                  <span className='unavailable-message text-sm'>No inward documents shared</span>
                )}
                {task.documentsShared?.map((document) => (
                  <MuiLink
                    key={document.id}
                    href={document.url}
                    target='_blank'
                    sx={{
                      textDecoration: 'underline',
                      color: (theme) => alpha(theme.palette.primary.light, 0.5),
                    }}
                  >
                    {document.name}
                  </MuiLink>
                ))}
              </div>
            </StyledGridItem>
          </StyledGrid>

          <StyledGrid columns={1}>
            <StyledGridItem columnIndex={0}>
              <span>Note/Comment</span>
              <span>{task.note}</span>
            </StyledGridItem>
          </StyledGrid>
        </div>
      </CollapsibleCard>

      <ConfirmationModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDeleteConfirm}
        title='Delete Assigned Task?'
        message='This action cannot be undone. Are you sure you want to delete this task?'
        confirmText='Delete'
        cancelText='Cancel'
      />
    </>
  );
}

// Project data
// {
//   "id": 1,
//   "name": "Test Project",
//   "date": "04/02/2026",
//   "code": "JOB020226",
//   "owner": "Architect 1",
//   "value": "₹59,000",
//   "totalMilestones": 4,
//   "completedMilestones": 0
// }

// Task data
// {
//   "id": 1,
//   "name": "S6.1 Design",
//   "status": "Assigned",
//   "employee": {
//       "id": 4,
//       "name": "Jhanvi Chaudry"
//   },
//   "allottedTime": "01:00:00",
//   "priority": "Medium",
//   "note": "Test",
//   "timeUsed": "00:00:00",
//   "documentsShared": [],
//   "submissions": []
// }
